import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Grid, InputBase, Link as MuiLink, Stack, Typography } from '@mui/material';
import { ArrowForward, Star } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SEO from '../components/SEO';
import { apiService, ApiError } from '../services/api';
import {
  BuiltForStrip,
  ChipTone,
  ClosingCta,
  ComparisonTable,
  DEEP,
  Eyebrow,
  FaqItem,
  FaqSection,
  FeatureRow,
  GREEN,
  HERO_IMAGE,
  HeroTitle,
  Highlight,
  INK,
  LINE,
  LINE_ON_DARK,
  Lede,
  MINT,
  MOCK_SHADOW,
  MUTED,
  MarketingPage,
  MockCard,
  MockPrimaryLine,
  MockRow,
  SectionTitle,
  SplitHeading,
  StatusChip,
  faqStructuredData,
  primaryButtonSx,
  secondaryButtonSx,
} from '../components/marketing/MarketingKit';

const FAQS: FaqItem[] = [
  {
    q: 'Can I set up services for my parents or other family members?',
    a: 'Yes. You can request service for any address, compare bids, choose a provider, and pay — all without being at the property. Many customers use LawnHub to take care of a parent’s lawn from another town or state.',
  },
  {
    q: 'Do I need to be there for estimates?',
    a: 'No. Providers bid from the details in your request and satellite imagery of the property. You compare bids online, check ratings and reviews, and accept the one you want — no appointment needed.',
  },
  {
    q: 'How are providers verified?',
    a: 'Providers who bid on new marketplace requests must submit liability insurance for LawnHub verification, and a pesticide applicator license for weed control or fertilizing bids. Providers who invite their existing customers onto LawnHub to use it as business software are not independently verified by LawnHub for insurance or licenses; those providers remain responsible for complying with the law. Ratings, reviews, and service history are visible for every provider.',
  },
  {
    q: 'How do payments work?',
    a: 'You save a card when you set up your account. Card details are entered through Stripe, our payment processor. After each completed visit, the charge is processed automatically and you get a receipt. Your full payment history is always in your account.',
  },
  {
    q: 'My lawn care company invited me. What does that mean?',
    a: 'Your provider uses LawnHub to run their business. Accepting the invitation sets up your account so you can save a payment method, see your schedule and service history, and get notified when visits are completed or moved. Your price and schedule are set by your provider.',
  },
  {
    q: 'What if my provider sends a service agreement?',
    a: 'You can review and sign it online from your account — no printing, scanning, or mailing. A copy stays with your account.',
  },
  {
    q: 'Can I get one-time jobs like a fall cleanup?',
    a: 'Yes. Your provider can send an estimate for one-time work such as cleanups, mulch, or aeration. You review and accept it online, and it is scheduled and paid like your regular service.',
  },
  {
    q: 'What happens at the end of the season?',
    a: 'If you have a seasonal contract, your provider can send a renewal offer for next season. You can accept it from your dashboard to keep the same provider without starting over.',
  },
  {
    q: 'What if I’m not satisfied with the service?',
    a: 'Providers are encouraged to add before-and-after photos, so you can see what was done. You can rate and review your provider after service, and our support team can help if something goes wrong.',
  },
  {
    q: 'Can I manage multiple properties?',
    a: 'Yes. Your home, a parent’s home, a rental — each property has its own services, schedule, and history, all in one account.',
  },
];

const HOW_IT_WORKS: Array<{ title: string; body: string }> = [
  {
    title: 'Describe the property',
    body: 'Enter the address, the services you want, and how often. Providers bid from your request and satellite imagery — nobody needs to come out for an estimate.',
  },
  {
    title: 'Compare bids and choose',
    body: 'See each provider’s price, rating, and reviews side by side. Accept the one that fits. No calls, no pressure.',
  },
  {
    title: 'Save a card and you’re done',
    body: 'Your provider schedules the visits. You’re charged after each completed visit, get a receipt, and can see every visit in your account.',
  },
];

const COMPARISON: string[][] = [
  ['Getting quotes', 'Call around and wait for callbacks', 'Bids arrive online'],
  ['Estimates', 'Be home when someone stops by', 'Providers bid from your request and satellite imagery'],
  ['Paying', 'Checks, cash, or invoices to keep track of', 'Card on file, charged after each completed visit'],
  ['Knowing it was done', 'Drive by, or take their word for it', 'Completion notice, photos when provided, full history'],
  ['A parent’s home or a rental', 'Juggle it by phone and text', 'Every property in one account'],
];

/* ---------- Service area check ---------- */

type ZipResult = { zipCode: string; providerCount: number; isServiced: boolean };

const ServiceAreaCheck: React.FC<{ onRequest: () => void }> = ({ onRequest }) => {
  const [zipCode, setZipCode] = useState('');
  const [result, setResult] = useState<ZipResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const zip = zipCode.trim();
    if (!/^\d{5}$/.test(zip)) {
      setError('Enter a 5-digit ZIP code.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiService.get<ZipResult>(`/providers/availability/${zip}`);
      if (!res.success || !res.data) {
        setError(res.error || 'We couldn’t check that ZIP code. Please try again.');
        return;
      }
      if (!res.data.isServiced) {
        try {
          await apiService.post('/admin/track-non-serviced-zip', { zipCode: zip, timestamp: new Date().toISOString() });
        } catch {
          // Tracking is best-effort; the result still shows.
        }
      }
      setResult({ zipCode: zip, providerCount: res.data.providerCount, isServiced: res.data.isServiced });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We couldn’t check that ZIP code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#fff', border: `1px solid ${LINE}`, borderRadius: 3, boxShadow: MOCK_SHADOW, p: { xs: 2.5, sm: 3 } }}>
      <Typography component="h2" sx={{ fontWeight: 800, fontSize: '1.15rem', color: INK, letterSpacing: '-0.01em' }}>
        Check your area
      </Typography>
      <Typography sx={{ fontSize: '0.9rem', color: MUTED, mt: 0.5, mb: 2 }}>
        See how many LawnHub providers serve your ZIP code.
      </Typography>
      <Box
        component="form"
        onSubmit={handleSearch}
        sx={{ display: 'flex', border: `1px solid ${LINE}`, borderRadius: 2, overflow: 'hidden', '&:focus-within': { borderColor: GREEN } }}
      >
        <InputBase
          value={zipCode}
          onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          placeholder="ZIP code"
          inputProps={{ inputMode: 'numeric', 'aria-label': 'ZIP code', autoComplete: 'postal-code' }}
          disabled={loading}
          sx={{ flex: 1, px: 2, py: 1, fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}
        />
        <Button
          type="submit"
          variant="contained"
          disabled={loading || zipCode.length === 0}
          sx={{ ...primaryButtonSx, borderRadius: 0, px: 2.5, minWidth: 96 }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Check'}
        </Button>
      </Box>
      {error && <Typography sx={{ mt: 1.5, fontSize: '0.85rem', color: '#a32a1c' }}>{error}</Typography>}
      {result && result.isServiced && (
        <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${LINE}` }}>
          <Stack direction="row" alignItems="baseline" spacing={1.25}>
            <Typography sx={{ fontSize: '2.25rem', fontWeight: 800, color: GREEN, letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {result.providerCount}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: INK }}>
              provider{result.providerCount === 1 ? '' : 's'} serve {result.zipCode}
            </Typography>
          </Stack>
          <Button onClick={onRequest} endIcon={<ArrowForward />} variant="contained" fullWidth sx={{ ...primaryButtonSx, mt: 2 }}>
            Request service
          </Button>
        </Box>
      )}
      {result && !result.isServiced && (
        <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${LINE}` }}>
          <Typography sx={{ fontWeight: 700, color: INK }}>We’re not in {result.zipCode} yet.</Typography>
          <Typography sx={{ fontSize: '0.9rem', color: MUTED, mt: 0.5 }}>
            We’ve noted your ZIP code — requests like yours help us decide where to grow next.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/* ---------- Product previews (static, illustrative) ---------- */

const PropertiesMock: React.FC = () => {
  const rows: Array<{ label: string; street: string; service: string; tone: ChipTone; status: string }> = [
    { label: 'Home', street: '12 Elm St', service: 'Mowing · Weekly · Next Thu', tone: 'green', status: 'Active' },
    { label: 'Mom & Dad', street: '88 Willow Rd', service: 'Mowing + fertilizing · Next Fri', tone: 'green', status: 'Active' },
    { label: 'Lake house', street: '4 Shore Dr', service: 'Snow removal · Season starts Nov 15', tone: 'grey', status: 'Upcoming' },
  ];
  return (
    <MockCard title="Your properties" meta="3 addresses">
      {rows.map((r, i) => (
        <MockRow key={r.street} last={i === rows.length - 1}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              bgcolor: MINT,
              color: DEEP,
              display: 'grid',
              placeItems: 'center',
              fontSize: '0.8rem',
              fontWeight: 800,
              flexShrink: 0,
            }}
          >
            {r.label.charAt(0)}
          </Box>
          <MockPrimaryLine title={`${r.label} · ${r.street}`} subtitle={r.service} />
          <StatusChip tone={r.tone}>{r.status}</StatusChip>
        </MockRow>
      ))}
      <Box sx={{ px: 2.25, py: 1.5, bgcolor: MINT, borderTop: `1px solid ${LINE}` }}>
        <Typography sx={{ fontSize: '0.7rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Next visit · Mom &amp; Dad
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: DEEP }}>Fri, Oct 2 · Greenline Lawn Co.</Typography>
      </Box>
    </MockCard>
  );
};

const BidsMock: React.FC = () => {
  const bids = [
    { name: 'Greenline Lawn Co.', rating: '4.9', reviews: 38, price: '$45', chosen: true },
    { name: 'Northside Turf', rating: '4.7', reviews: 21, price: '$42', chosen: false },
    { name: 'Hollis Outdoor', rating: '4.8', reviews: 54, price: '$50', chosen: false },
  ];
  return (
    <MockCard title="Bids for 88 Willow Rd" meta="Weekly mowing · 3 bids">
      {bids.map((b, i) => (
        <MockRow key={b.name} last={i === bids.length - 1}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: INK }} noWrap>
              {b.name}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
              <Star sx={{ fontSize: 14, color: '#e6a100' }} />
              <Typography sx={{ fontSize: '0.75rem', color: MUTED }}>
                {b.rating} · {b.reviews} reviews
              </Typography>
              <StatusChip tone="green">Insured</StatusChip>
            </Stack>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}>
              {b.price}
              <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, color: MUTED }}>
                /visit
              </Box>
            </Typography>
            {b.chosen && (
              <Box sx={{ mt: 0.5, bgcolor: GREEN, color: '#fff', fontSize: '0.7rem', fontWeight: 700, px: 1.25, py: 0.4, borderRadius: 1.5 }}>
                Accept bid
              </Box>
            )}
          </Box>
        </MockRow>
      ))}
    </MockCard>
  );
};

const VisitHistoryMock: React.FC = () => {
  const items: Array<{ date: string; title: string; detail: string; tone: ChipTone; label: string }> = [
    { date: 'Tue, Sep 29', title: 'Completed at 10:42 AM', detail: '4 photos · $45.00 charged · receipt emailed', tone: 'green', label: 'Done' },
    { date: 'Tue, Sep 22', title: 'Moved to Wednesday', detail: 'Rain delay — you were notified', tone: 'amber', label: 'Rescheduled' },
    { date: 'Tue, Sep 15', title: 'Completed at 9:58 AM', detail: '3 photos · $45.00 charged', tone: 'green', label: 'Done' },
    { date: 'Apr 2', title: 'Service agreement signed', detail: '2026 Season Agreement.pdf', tone: 'grey', label: 'Signed' },
  ];
  return (
    <MockCard title="88 Willow Rd" meta="Visit history">
      {items.map((it, i) => (
        <MockRow key={it.date + it.title} last={i === items.length - 1}>
          <Typography sx={{ width: 78, flexShrink: 0, fontSize: '0.75rem', color: MUTED }}>{it.date}</Typography>
          <MockPrimaryLine title={it.title} subtitle={it.detail} />
          <StatusChip tone={it.tone}>{it.label}</StatusChip>
        </MockRow>
      ))}
    </MockCard>
  );
};

/* ---------- Page ---------- */

const CustomerBenefitsPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    navigate('/register', { state: { userType: 'customer' } });
  };

  return (
    <>
      <SEO
        title="Lawn Care for Homeowners | LawnHub"
        description="Compare bids from insurance-verified lawn care providers, set up service for a parent’s home from anywhere, and pay automatically after each visit with LawnHub."
        keywords="lawn care for parents, remote lawn service setup, hire lawn care, verified lawn care providers, compare lawn service prices, snow removal request, LawnHub customers"
        url="/customer-benefits"
        structuredData={faqStructuredData(FAQS)}
      />

      <MarketingPage>
        {/* Hero */}
        <Box component="section" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 7, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Eyebrow>LawnHub for homeowners</Eyebrow>
                <HeroTitle>
                  Lawn care you can set up <Highlight>from anywhere.</Highlight>
                </HeroTitle>
                <Lede maxWidth={520}>
                  Compare bids from insured local pros, choose one, and pay automatically after each visit. For your
                  own home, a parent’s, or a rental — without waiting around for estimates.
                </Lede>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                  <Button variant="contained" size="large" onClick={handleGetStarted} endIcon={<ArrowForward />} sx={primaryButtonSx}>
                    Request service
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                    sx={secondaryButtonSx}
                  >
                    How it works
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ position: 'relative', pt: { md: 7 }, pb: { md: 14 }, pl: { md: 6 } }}>
                  <Box
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: '78%',
                      borderRadius: 4,
                      backgroundImage: `url("${HERO_IMAGE}")`,
                      backgroundSize: 'cover',
                      backgroundPosition: '30% center',
                    }}
                  />
                  <Box sx={{ position: 'relative', maxWidth: 400 }}>
                    <ServiceAreaCheck onRequest={handleGetStarted} />
                  </Box>
                  <Box
                    aria-hidden
                    sx={{
                      display: { xs: 'none', md: 'block' },
                      position: 'absolute',
                      right: 24,
                      bottom: 24,
                      bgcolor: DEEP,
                      color: '#fff',
                      borderRadius: 3,
                      px: 2.5,
                      py: 2,
                      boxShadow: MOCK_SHADOW,
                      minWidth: 220,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b9d8bd' }}>
                      88 Willow Rd · Today
                    </Typography>
                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, letterSpacing: '-0.01em' }}>Visit completed</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>10:42 AM · 4 photos · receipt emailed</Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <BuiltForStrip
          label="Services"
          items={['Mowing & trimming', 'Weed control & fertilizing', 'Snow removal', 'Seasonal contracts', 'One-time cleanups']}
        />

        {/* How it works */}
        <Box id="how-it-works" component="section" sx={{ py: { xs: 7, md: 11 }, borderTop: `1px solid ${LINE}`, scrollMarginTop: 80 }}>
          <Container maxWidth="lg">
            <SplitHeading
              eyebrow="How it works"
              title="Three steps, and the lawn takes care of itself."
              body="From request to accepted bid, everything happens online — no phone calls or appointments."
            />
            <Grid container spacing={{ xs: 4, md: 6 }}>
              {HOW_IT_WORKS.map((s, i) => (
                <Grid item xs={12} md={4} key={s.title}>
                  <Box sx={{ borderTop: `2px solid ${INK}`, pt: 2.5 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: GREEN, fontVariantNumeric: 'tabular-nums', mb: 1.5 }}>
                      {String(i + 1).padStart(2, '0')}
                    </Typography>
                    <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.2rem', color: INK, mb: 1 }}>
                      {s.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.65, color: MUTED }}>{s.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <FeatureRow
          eyebrow="For family"
          title="Take care of Mom and Dad’s yard from three states away."
          body="Set up service at any address, pick the provider, and handle payment yourself. Each property keeps its own services, schedule, and history — so a parent’s home, a rental, and your own yard all live in one account."
          points={[
            'Request service for any address — you don’t need to be there',
            'Every property in one account, each with its own schedule',
            'Notifications when visits are completed or moved',
          ]}
          visual={<PropertiesMock />}
        />

        <FeatureRow
          reverse
          eyebrow="Compare bids"
          title="Real prices from real providers, side by side."
          body="Providers who bid on marketplace requests are insurance-verified by LawnHub before they can bid. Compare price, rating, and reviews, then accept the one you want."
          points={[
            'Liability insurance verified for every marketplace bidder',
            'Pesticide applicator license verified for weed control and fertilizing bids',
            'Ratings and reviews from other LawnHub customers',
          ]}
          visual={<BidsMock />}
        />

        <FeatureRow
          eyebrow="After you book"
          title="Know it was done, without driving by."
          body="You’re notified when each visit is completed, with before-and-after photos when your provider adds them. If weather moves a visit, you hear about it. The charge happens after the work is done, and every receipt is in your account."
          points={[
            'Automatic payment after each completed visit, with receipts',
            'Seasonal contracts paid up front or monthly, and next-season renewals',
            'Sign your provider’s service agreement online',
            'Accept estimates for one-time jobs like fall cleanup',
          ]}
          visual={<VisitHistoryMock />}
        />

        {/* Verification */}
        <Box component="section" sx={{ bgcolor: DEEP, color: '#fff', py: { xs: 8, md: 11 } }}>
          <Container maxWidth="lg">
            <SplitHeading
              light
              eyebrow="Verification"
              title="What “verified” means on LawnHub."
              body="We’d rather be specific than vague. Here’s exactly what we check, and when."
            />
            <Grid container sx={{ borderTop: `1px solid ${LINE_ON_DARK}` }}>
              {[
                {
                  title: 'Marketplace providers',
                  body: 'Anyone bidding on a new request must submit liability insurance, which LawnHub verifies before they can bid. Weed control and fertilizing bids also require a verified pesticide applicator license.',
                },
                {
                  title: 'Providers who invite you',
                  body: 'If your current lawn care company invites you to LawnHub, they use it as business software. LawnHub does not independently verify their insurance or licenses — they remain responsible for complying with the law.',
                },
                {
                  title: 'Every provider',
                  body: 'Ratings, reviews, and service history are visible, and providers are encouraged to document each visit with before-and-after photos.',
                },
              ].map((c, i) => (
                <Grid
                  item
                  xs={12}
                  md={4}
                  key={c.title}
                  sx={{
                    py: { xs: 3, md: 4 },
                    pr: { md: 4 },
                    pl: { md: i === 0 ? 0 : 4 },
                    borderLeft: { md: i === 0 ? 'none' : `1px solid ${LINE_ON_DARK}` },
                    borderBottom: { xs: `1px solid ${LINE_ON_DARK}`, md: 'none' },
                  }}
                >
                  <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
                    {c.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.72)' }}>{c.body}</Typography>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Comparison */}
        <Box component="section" sx={{ py: { xs: 7, md: 11 } }}>
          <Container maxWidth="lg">
            <Eyebrow>The difference</Eyebrow>
            <SectionTitle>Hiring lawn care, minus the phone tag.</SectionTitle>
            <ComparisonTable columns={['', 'The usual way', 'With LawnHub']} rows={COMPARISON} highlightColumn={2} />
          </Container>
        </Box>

        <FaqSection
          title="Questions homeowners ask."
          faqs={FAQS}
          aside={
            <>
              Run a lawn care business?{' '}
              <MuiLink component={RouterLink} to="/provider-benefits" sx={{ color: GREEN, fontWeight: 600 }}>
                See LawnHub for providers
              </MuiLink>
              .
            </>
          }
        />

        <ClosingCta
          title="Get your first bids this week."
          body="Create a free account, add the property, and tell us what you need. Providers in your area will take it from there."
          primary={{ label: 'Request service', onClick: handleGetStarted }}
          secondary={{ label: 'About LawnHub', to: '/about-us' }}
        />
      </MarketingPage>
    </>
  );
};

export default CustomerBenefitsPage;
